import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosClient from "../../api/axiosClient";
import {
  Zap,
  RotateCcw,
  ShieldCheck,
  Database,
  Monitor,
  Search,
  RefreshCw,
  AlertTriangle,
  Layers,
  Cpu,
  Fingerprint,
  CheckCircle,
  Activity,
  Server,
} from "lucide-react";

const safeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.actions)) return value.actions;
  return [];
};

const shortAgent = (agentId) => {
  if (!agentId) return "unknown";
  return String(agentId).split("-")[0];
};

const normalizeConfidence = (value) => {
  const raw = Number(value || 0);
  if (Number.isNaN(raw)) return 0;
  if (raw > 1) return Math.min(raw, 100);
  return Math.min(raw * 100, 100);
};

const getActionTime = (action) =>
  action.executed_at || action.created_at || action.killed_at || action.timestamp;

const formatDateTime = (value) => {
  try {
    if (!value) return "N/A";
    return new Date(value).toLocaleString();
  } catch {
    return "N/A";
  }
};

const getAgentId = (action) => action.agent_id || action.device_agent_id || "unknown";

const getHostname = (action) =>
  action.hostname || action.device_hostname || action.agent_hostname || "Unknown Device";

const getActionLabel = (action) =>
  action.action_type ||
  action.action ||
  action.decision ||
  action.threat_type ||
  "Autonomous Decision";

const getReason = (action) =>
  action.reasoning ||
  action.reason ||
  action.ai_reason ||
  action.details ||
  "Autonomous SOC decision recorded by AIRS policy engine.";

const SocHistoryPage = () => {
  const queryClient = useQueryClient();

  const [selectedAgent, setSelectedAgent] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showReversed, setShowReversed] = useState(true);

  const {
    data: rawActions = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["socHistory"],
    queryFn: async () => {
      const response = await axiosClient.get("/admin/soc/history?limit=500");
      return safeArray(response);
    },
    refetchInterval: 5000,
  });

  const actions = useMemo(() => safeArray(rawActions), [rawActions]);

  const devices = useMemo(() => {
    const map = new Map();

    actions.forEach((action) => {
      const agentId = getAgentId(action);

      if (!map.has(agentId)) {
        map.set(agentId, {
          agent_id: agentId,
          hostname: getHostname(action),
          total: 0,
          active: 0,
          reversed: 0,
          last_seen: getActionTime(action),
          max_confidence: 0,
        });
      }

      const item = map.get(agentId);
      item.total += 1;

      if (action.is_reversed) item.reversed += 1;
      else item.active += 1;

      item.max_confidence = Math.max(
        item.max_confidence,
        normalizeConfidence(action.confidence)
      );

      const current = new Date(getActionTime(action) || 0).getTime();
      const previous = new Date(item.last_seen || 0).getTime();
      if (current > previous) item.last_seen = getActionTime(action);
    });

    return Array.from(map.values()).sort(
      (a, b) => new Date(b.last_seen || 0) - new Date(a.last_seen || 0)
    );
  }, [actions]);

  const filteredActions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return actions.filter((action) => {
      const agentId = getAgentId(action);

      if (selectedAgent !== "all" && agentId !== selectedAgent) return false;
      if (!showReversed && action.is_reversed) return false;

      if (query) {
        const matched =
          getHostname(action).toLowerCase().includes(query) ||
          agentId.toLowerCase().includes(query) ||
          getActionLabel(action).toLowerCase().includes(query) ||
          String(getReason(action)).toLowerCase().includes(query);

        if (!matched) return false;
      }

      return true;
    });
  }, [actions, selectedAgent, searchTerm, showReversed]);

  const visibleStats = useMemo(() => {
    const active = filteredActions.filter((x) => !x.is_reversed).length;
    const reversed = filteredActions.filter((x) => x.is_reversed).length;
    const maxConfidence = filteredActions.length
      ? Math.max(...filteredActions.map((x) => normalizeConfidence(x.confidence)))
      : 0;

    return {
      total: filteredActions.length,
      active,
      reversed,
      maxConfidence,
    };
  }, [filteredActions]);

  const undoMutation = useMutation({
    mutationFn: (id) => axiosClient.post(`/admin/soc/undo/${id}`),
    onSuccess: (res) => {
      alert(res?.message || "SOC decision reversed. Agent notified.");
      queryClient.invalidateQueries(["socHistory"]);
    },
  });

  return (
    <div className="w-full max-w-full overflow-hidden space-y-6 pb-10">
      <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 p-5 sm:p-7 lg:p-8 shadow-2xl">
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-emerald-500/10 blur-3xl" />

        <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-[0.25em] text-emerald-400">
                Neural Uplink Active
              </span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-black tracking-tight text-white">
              Autonomous SOC Ledger
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-400">
              Device-wise autonomous decision history for AIRS security actions.
              Select a device to review only that endpoint’s SOC activity.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
            <div className="rounded-2xl border border-blue-500/20 bg-blue-500/10 p-4 min-w-[130px]">
              <p className="text-[10px] uppercase tracking-widest text-blue-300">
                Visible
              </p>
              <p className="mt-1 text-3xl font-black text-white">
                {visibleStats.total}
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4 min-w-[130px]">
              <p className="text-[10px] uppercase tracking-widest text-emerald-300">
                Secured
              </p>
              <p className="mt-1 text-3xl font-black text-emerald-300">
                {visibleStats.active}
              </p>
            </div>

            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 min-w-[130px]">
              <p className="text-[10px] uppercase tracking-widest text-amber-300">
                Reversed
              </p>
              <p className="mt-1 text-3xl font-black text-amber-300">
                {visibleStats.reversed}
              </p>
            </div>

            <div className="rounded-2xl border border-purple-500/20 bg-purple-500/10 p-4 min-w-[130px]">
              <p className="text-[10px] uppercase tracking-widest text-purple-300">
                Confidence
              </p>
              <p className="mt-1 text-3xl font-black text-purple-300">
                {visibleStats.maxConfidence.toFixed(0)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-800 bg-slate-950/90 p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by device, agent ID, action, or reasoning..."
              className="w-full rounded-2xl border border-slate-800 bg-slate-900 px-10 py-3 text-sm text-white outline-none placeholder:text-slate-600 focus:border-blue-500/60"
            />
          </div>

          <label className="flex items-center gap-2 rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={showReversed}
              onChange={(e) => setShowReversed(e.target.checked)}
            />
            Show reversed
          </label>

          <button
            onClick={() => refetch()}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-xs font-bold uppercase tracking-wider text-blue-300 hover:bg-blue-500/20"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="mt-5 flex max-w-full gap-3 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedAgent("all")}
            className={`shrink-0 rounded-2xl border px-4 py-3 text-left transition ${
              selectedAgent === "all"
                ? "border-blue-500/50 bg-blue-500/15"
                : "border-slate-800 bg-slate-900 hover:bg-slate-800"
            }`}
          >
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-blue-400" />
              <div>
                <p className="text-sm font-black text-white">All Fleet</p>
                <p className="text-[10px] text-slate-500">
                  {actions.length} records
                </p>
              </div>
            </div>
          </button>

          {devices.map((device) => (
            <button
              key={device.agent_id}
              onClick={() => setSelectedAgent(device.agent_id)}
              className={`shrink-0 rounded-2xl border px-4 py-3 text-left transition min-w-[220px] ${
                selectedAgent === device.agent_id
                  ? "border-emerald-500/50 bg-emerald-500/10"
                  : "border-slate-800 bg-slate-900 hover:bg-slate-800"
              }`}
            >
              <div className="flex items-center gap-3">
                <Server className="h-5 w-5 text-slate-300" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-black text-white">
                    {device.hostname}
                  </p>
                  <p className="text-[10px] font-mono text-slate-500">
                    {shortAgent(device.agent_id)} · {device.total} records
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-3xl border border-dashed border-slate-800 bg-slate-950 py-20 text-center">
          <Activity className="mx-auto mb-4 h-12 w-12 animate-pulse text-blue-500" />
          <p className="font-mono text-sm text-slate-500">
            Loading autonomous SOC ledger...
          </p>
        </div>
      ) : isError ? (
        <div className="rounded-3xl border border-red-500/20 bg-red-950/20 py-20 text-center">
          <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <p className="font-mono text-sm text-red-300">
            Failed to load SOC ledger.
          </p>
          <p className="mt-2 text-xs text-red-500/70">
            {error?.message || "Unknown error"}
          </p>
        </div>
      ) : filteredActions.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-800 bg-slate-950 py-20 text-center">
          <Database className="mx-auto mb-4 h-12 w-12 text-slate-700" />
          <p className="font-mono text-sm text-slate-500">
            No autonomous actions recorded for selected scope.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {filteredActions.map((action) => {
            const confidence = normalizeConfidence(action.confidence);
            const isReversed = Boolean(action.is_reversed);
            const agentId = getAgentId(action);
            const actionTime = getActionTime(action);

            return (
              <div
                key={action.id}
                className={`overflow-hidden rounded-3xl border bg-slate-950 shadow-xl transition hover:-translate-y-0.5 hover:bg-slate-900/90 ${
                  isReversed
                    ? "border-amber-500/30"
                    : "border-slate-800 hover:border-blue-500/30"
                }`}
              >
                <div className="flex flex-col gap-4 p-5 sm:p-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                          isReversed
                            ? "bg-amber-500/10 text-amber-400"
                            : "bg-emerald-500/10 text-emerald-400"
                        }`}
                      >
                        {isReversed ? (
                          <RotateCcw className="h-6 w-6" />
                        ) : (
                          <ShieldCheck className="h-6 w-6" />
                        )}
                      </div>

                      <div className="min-w-0">
                        <p className="truncate text-base font-black text-white">
                          {getActionLabel(action)}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {formatDateTime(actionTime)}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`w-fit rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${
                        isReversed
                          ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                          : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                      }`}
                    >
                      {isReversed ? "Reversed" : "Secured"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                      <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        <Monitor className="h-3 w-3" />
                        Device
                      </div>
                      <p className="truncate text-sm font-bold text-white">
                        {getHostname(action)}
                      </p>
                      <p className="mt-1 truncate font-mono text-[10px] text-slate-500">
                        {shortAgent(agentId)}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                      <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        <Zap className="h-3 w-3" />
                        Confidence
                      </div>
                      <p className="text-2xl font-black text-blue-300">
                        {confidence.toFixed(0)}%
                      </p>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800">
                        <div
                          className="h-full rounded-full bg-blue-500"
                          style={{ width: `${confidence}%` }}
                        />
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                      <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        <Cpu className="h-3 w-3" />
                        Engine
                      </div>
                      <p className="text-sm font-bold text-white">
                        AIRS-X
                      </p>
                      <p className="mt-1 text-[10px] text-slate-500">
                        Autonomous SOC
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-800 bg-black/30 p-4">
                    <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-blue-400">
                      <Fingerprint className="h-3 w-3" />
                      AI SOC Reasoning
                    </div>
                    <p className="break-words text-sm italic leading-relaxed text-slate-300">
                      “{getReason(action)}”
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 break-all font-mono text-[10px] text-slate-600">
                      ID: {action.id} · Agent: {agentId}
                    </div>

                    {!isReversed ? (
                      <button
                        onClick={() => undoMutation.mutate(action.id)}
                        disabled={undoMutation.isLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-amber-300 transition hover:bg-amber-500/20 disabled:opacity-50"
                      >
                        <RotateCcw
                          className={`h-4 w-4 ${
                            undoMutation.isLoading ? "animate-spin" : ""
                          }`}
                        />
                        Undo Decision
                      </button>
                    ) : (
                      <div className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-slate-500">
                        <CheckCircle className="h-4 w-4" />
                        Handled
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SocHistoryPage;